"use client";

import { useState } from "react";

type TabId = "docker-mcp" | "claude-code" | "opencode" | "codex" | "windsurf";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  cliCommand?: string;
  config: string;
  configFile: string;
  note?: string;
}

const tabs: Tab[] = [
  {
    id: "claude-code",
    label: "Claude Code",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5zm0 18.5c-3.72-1.06-6.5-4.89-6.5-8.5V8.16L12 4.82l6.5 3.34V12c0 3.61-2.78 7.44-6.5 8.5z"/>
      </svg>
    ),
    cliCommand: "claude mcp add code-pathfinder -- pathfinder serve --project /absolute/path/to/your/project",
    config: `{
  "mcpServers": {
    "code-pathfinder": {
      "command": "pathfinder",
      "args": [
        "serve",
        "--project",
        "/absolute/path/to/your/project"
      ]
    }
  }
}`,
    configFile: "~/.config/claude/mcp_config.json",
    note: "macOS & Linux • JSON format",
  },
  {
    id: "opencode",
    label: "Open Code",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/>
      </svg>
    ),
    config: `{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "code-pathfinder": {
      "type": "local",
      "command": ["pathfinder", "serve", "--project", "/absolute/path/to/your/project"],
      "enabled": true
    }
  }
}`,
    configFile: "~/.config/opencode/opencode.json",
    note: "All platforms • JSON format",
  },
  {
    id: "codex",
    label: "Codex",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
      </svg>
    ),
    cliCommand: "codex mcp add code-pathfinder -- pathfinder serve --project /absolute/path/to/your/project",
    config: `[mcp_servers.code-pathfinder]
command = "pathfinder"
args = ["serve", "--project", "/absolute/path/to/your/project"]`,
    configFile: "~/.codex/config.toml",
    note: "All platforms • TOML format",
  },
  {
    id: "windsurf",
    label: "Windsurf",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M2.88 18.05c.25.58.62.99 1.13 1.24.35.18.74.27 1.17.27.82 0 1.61-.3 2.37-.89.87-.68 1.79-1.76 2.75-3.23 1.1-1.69 2.02-3.48 2.76-5.37.74-1.89 1.11-3.61 1.11-5.16 0-.63-.08-1.19-.24-1.67-.25-.72-.7-1.24-1.35-1.55-.31-.15-.65-.22-1.02-.22-.71 0-1.41.26-2.1.77-.84.63-1.73 1.64-2.67 3.02-1.07 1.58-1.97 3.26-2.7 5.04C3.31 12.3 2.88 14 2.88 15.5c0 .87.1 1.64.3 2.29.15.52.35.96.6 1.32.23.34.47.6.7.79zm9.31-5.45c-.47 1.2-1.03 2.37-1.67 3.5-.76 1.34-1.58 2.45-2.46 3.31-.66.64-1.29 1.08-1.89 1.31-.37.14-.72.21-1.05.21-.19 0-.36-.03-.51-.08-.24-.09-.39-.25-.46-.47-.05-.16-.08-.38-.08-.67 0-1.22.3-2.62.89-4.18.59-1.57 1.37-3.16 2.34-4.78.84-1.4 1.72-2.59 2.63-3.56.68-.73 1.35-1.23 2-1.51.4-.17.78-.26 1.14-.26.15 0 .28.02.39.06.18.07.29.19.33.36.03.11.05.27.05.49 0 1.25-.33 2.7-.98 4.34-.49 1.23-1.12 2.49-1.88 3.77zm8.94 5.87c.23.53.53.92.89 1.16.25.17.53.25.84.25.58 0 1.13-.21 1.65-.62.61-.48 1.25-1.24 1.93-2.26.78-1.18 1.42-2.43 1.93-3.76.51-1.33.77-2.53.77-3.61 0-.44-.06-.83-.17-1.17-.18-.5-.49-.87-.94-1.09-.22-.11-.45-.16-.71-.16-.5 0-.99.18-1.47.54-.59.44-1.21 1.15-1.87 2.12-.75 1.11-1.38 2.29-1.89 3.53-.51 1.24-.77 2.39-.77 3.45 0 .61.07 1.15.21 1.61.11.37.25.67.42.92.16.24.33.42.49.55z"/>
      </svg>
    ),
    config: `{
  "mcpServers": {
    "code-pathfinder": {
      "command": "pathfinder",
      "args": [
        "serve",
        "--project",
        "/absolute/path/to/your/project"
      ]
    }
  }
}`,
    configFile: "~/.codeium/windsurf/mcp_config.json",
    note: "All platforms • Configure via MCP Marketplace or edit JSON",
  },
  {
    id: "docker-mcp",
    label: "Docker MCP",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13.983 11.078h2.119a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.119a.185.185 0 00-.185.185v1.888c0 .102.083.185.185.185m-2.954-5.43h2.118a.186.186 0 00.186-.186V3.574a.186.186 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.186m0 2.716h2.118a.187.187 0 00.186-.186V6.29a.186.186 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.887c0 .102.082.185.185.186m-2.93 0h2.12a.186.186 0 00.184-.186V6.29a.185.185 0 00-.185-.185H8.1a.185.185 0 00-.185.185v1.887c0 .102.083.185.185.186m-2.964 0h2.119a.186.186 0 00.185-.186V6.29a.186.186 0 00-.185-.185H5.136a.186.186 0 00-.186.185v1.887c0 .102.084.185.186.186m5.893 2.715h2.118a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.185m-2.93 0h2.12a.185.185 0 00.184-.185V9.006a.185.185 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.083.185.185.185m-2.964 0h2.119a.185.185 0 00.185-.185V9.006a.186.186 0 00-.185-.186H5.136a.186.186 0 00-.186.186v1.887c0 .102.084.185.186.185m-2.92 0h2.12a.185.185 0 00.184-.185V9.006a.186.186 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.082.185.185.185M23.763 9.89c-.065-.051-.672-.51-1.954-.51-.338.001-.676.03-1.01.087-.248-1.7-1.653-2.53-1.716-2.566l-.344-.199-.226.327c-.284.438-.49.922-.612 1.43-.23.97-.09 1.882.403 2.661-.595.332-1.55.413-1.744.42H.751a.751.751 0 00-.75.748 11.376 11.376 0 00.692 4.062c.545 1.428 1.355 2.48 2.41 3.124 1.18.723 3.1 1.137 5.275 1.137.983.003 1.963-.086 2.93-.266a12.248 12.248 0 003.823-1.389c.98-.567 1.86-1.288 2.61-2.136 1.252-1.418 1.998-2.997 2.553-4.4h.221c1.372 0 2.215-.549 2.68-1.009.309-.293.55-.65.707-1.046l.098-.288z"/>
      </svg>
    ),
    cliCommand: "docker mcp server enable code-pathfinder && docker mcp client connect claude-code",
    config: `# Step 1: Enable the server
docker mcp server enable code-pathfinder

# Step 2: Set your project path
cat > ~/.docker/mcp/config.yaml << EOF
code-pathfinder:
  path: /absolute/path/to/your/project
EOF

# Step 3: Connect your AI assistant
docker mcp client connect claude-code`,
    configFile: "Docker Desktop → MCP Toolkit → Catalog",
    note: "Requires Docker Desktop 4.62+ with MCP Toolkit enabled • Works with all MCP clients",
  },
];

export default function AIAssistantTabs() {
  const [activeTab, setActiveTab] = useState<TabId>("claude-code");
  const [copied, setCopied] = useState(false);
  const [cliCopied, setCliCopied] = useState(false);

  const activeTabData = tabs.find((tab) => tab.id === activeTab)!;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(activeTabData.config);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const copyCliCommand = async () => {
    try {
      if (activeTabData.cliCommand) {
        await navigator.clipboard.writeText(activeTabData.cliCommand);
        setCliCopied(true);
        setTimeout(() => setCliCopied(false), 2000);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Tab buttons */}
      <div className="flex border-b border-gray-200 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 sm:px-8 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.id
                ? "text-gray-900 border-lime-500"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* CLI Command (if available) */}
      {activeTabData.cliCommand && (
        <div className="mt-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">Quick setup with CLI:</p>
          <div className="relative group">
            <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-100 break-all relative pr-12">
              {activeTabData.cliCommand}
              <button
                onClick={copyCliCommand}
                className="absolute top-3 right-3 p-2 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
                title="Copy to clipboard"
              >
                {cliCopied ? (
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config file location */}
      <div className="mt-6 mb-3">
        <p className="text-sm font-semibold text-gray-700">
          Or edit configuration file directly:
        </p>
        <p className="text-xs text-gray-600 mt-1">
          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">{activeTabData.configFile}</code>
        </p>
      </div>

      {/* Config display */}
      <div className="relative group">
        <div className="bg-gray-900 rounded-lg p-6 font-mono text-sm text-gray-100 relative">
          <button
            onClick={copyToClipboard}
            className="absolute top-3 right-3 p-2 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
            title="Copy to clipboard"
          >
            {copied ? (
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
          <pre className="overflow-x-auto">{activeTabData.config}</pre>
        </div>
        {activeTabData.note && (
          <p className="mt-3 text-sm text-gray-500 text-center">{activeTabData.note}</p>
        )}
      </div>

    </div>
  );
}
